import { motion } from "framer-motion";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowDownRight,
  ArrowUpRight,
  BarChart2,
  Download,
  LineChart as LineChartIcon,
  TrendingUp,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { cn } from "@repo/core";
import { AppCard } from "./ui/AppCard";
import { useCurrency } from "../hooks/useCurrency";
import { monthLongLabel, monthShortLabel } from "../i18n/dates";
import { useT } from "../i18n/useT";
import {
  cropToPopulatedRange,
  ENABLE_CHART_PNG_EXPORT,
  exportCanvasChartPng,
  periodLabelFromMonths,
  resolveThemeChartColors,
} from "../lib/exportUtils";
import type { MonthlySummary } from "../lib/schema";

type ChartViewMode = "bars" | "area";

interface MonthlyCashFlowChartProps {
  monthly: MonthlySummary[];
  year: number;
}

// ── Custom Glassmorphism Tooltip ─────────────────────────
function CustomCashFlowTooltip({
  active,
  payload,
}: TooltipProps<number, string>) {
  const { format } = useCurrency();
  const { t } = useT();
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload as { fullName?: string; name?: string } | undefined;
  const fullMonth = row?.fullName ?? row?.name ?? "";

  const income = Number(payload.find((p) => p.dataKey === "income")?.value ?? 0);
  const expense = Number(payload.find((p) => p.dataKey === "expense")?.value ?? 0);
  const net = Number(payload.find((p) => p.dataKey === "net")?.value ?? (income - expense));
  const rate = income > 0 ? ((net / income) * 100).toFixed(1) : "0.0";
  const isPositive = net >= 0;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className="rounded-xl border border-border/80 bg-card/95 backdrop-blur-md shadow-xl p-3.5 text-xs min-w-[190px] z-50"
      style={{ boxShadow: "var(--card-shadow-hover)" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between pb-2 border-b border-border/60 mb-2">
        <span className="font-semibold text-foreground text-sm">{fullMonth}</span>
        <span
          className={cn(
            "text-[10px] font-bold px-2 py-0.5 rounded-full",
            isPositive
              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300"
              : "bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300"
          )}
        >
          {t("cf.pctSaved", { n: rate })}
        </span>
      </div>

      {/* Metrics */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <span
              className="w-2.5 h-2.5 rounded-sm inline-block shadow-sm"
              style={{ background: "hsl(var(--primary))" }}
            />
            <span>{t("cf.income")}</span>
          </div>
          <span className="font-semibold text-foreground">{format(income)}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <span
              className="w-2.5 h-2.5 rounded-sm inline-block shadow-sm"
              style={{ background: "hsl(10 65% 55%)" }}
            />
            <span>{t("cf.expenses")}</span>
          </div>
          <span className="font-semibold text-foreground">{format(expense)}</span>
        </div>

        <div className="pt-2 mt-1 border-t border-border/60 flex items-center justify-between">
          <div className="flex items-center gap-1 text-muted-foreground font-medium">
            {isPositive ? (
              <ArrowUpRight size={13} className="text-emerald-500" />
            ) : (
              <ArrowDownRight size={13} className="text-destructive" />
            )}
            <span>{t("cf.netFlow")}</span>
          </div>
          <span
            className={cn(
              "font-bold text-sm",
              isPositive
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-destructive"
            )}
          >
            {isPositive ? "+" : ""}
            {format(net)}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

export function MonthlyCashFlowChart({ monthly, year }: MonthlyCashFlowChartProps) {
  const { format } = useCurrency();
  const { t, locale } = useT();
  const [viewMode, setViewMode] = useState<ChartViewMode>("bars");

  // Chart data — crop trailing/leading empty months so the live chart & PNG match
  const chartData = useMemo(() => {
    const full = monthly.map((m, i) => ({
      name: monthShortLabel(i + 1, locale, year),
      fullName: monthLongLabel(i + 1, locale, year),
      income: Math.round(m.totalIncome),
      expense: Math.round(m.totalExpense),
      net: Math.round(m.leftover),
      savingsRate: m.savingsRate,
    }));
    return cropToPopulatedRange(
      full,
      (r) => r.income > 0 || r.expense > 0 || Math.abs(r.net) > 0
    );
  }, [monthly, locale, year]);

  const handleExportPng = () => {
    const colors = resolveThemeChartColors();
    const labels = chartData.map((d) => d.name);
    exportCanvasChartPng({
      filename: `cash-flow-${year}`,
      title: t("cf.titleYear", { year }),
      labels,
      series: [
        { label: t("cf.income"), color: colors.income, values: chartData.map((d) => d.income) },
        { label: t("cf.expenses"), color: colors.expense, values: chartData.map((d) => d.expense) },
      ],
      watermark: periodLabelFromMonths(labels, year),
      kind: viewMode === "bars" ? "bars" : "lines",
      formatTick: (n) => format(n, true),
    });
  };

  // Aggregate stats
  const totalIncome = monthly.reduce((s, m) => s + m.totalIncome, 0);
  const totalExpense = monthly.reduce((s, m) => s + m.totalExpense, 0);
  const totalNet = totalIncome - totalExpense;

  const activeMonths = monthly.filter((m) => m.totalIncome > 0 || m.totalExpense > 0).length || 1;
  const avgIncome = totalIncome / activeMonths;
  const avgExpense = totalExpense / activeMonths;
  const avgNet = totalNet / activeMonths;

  // Best income and highest expense months
  const bestIncome = useMemo(() => {
    return [...chartData].sort((a, b) => b.income - a.income)[0];
  }, [chartData]);

  const highestExpense = useMemo(() => {
    return [...chartData].sort((a, b) => b.expense - a.expense)[0];
  }, [chartData]);

  return (
    <AppCard padding="none" className="overflow-hidden border border-border shadow-md">
      {/* ── Top Header Bar ───────────────────────────────── */}
      <div className="p-4 sm:p-5 border-b border-border bg-card/60 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h3 className="text-base font-semibold text-foreground tracking-tight">
              {t("cf.title")}
            </h3>
            <span
              className={cn(
                "app-badge text-[10px] font-semibold",
                totalNet >= 0
                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"
                  : "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200"
              )}
            >
              {totalNet >= 0 ? "+" : ""}
              {format(totalNet, true)} {t("cf.netSurplus")}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("cf.titleYear", { year })}
          </p>
        </div>

        {/* View Switcher Controls */}
        <div className="flex items-center gap-1.5 self-start md:self-auto">
          {ENABLE_CHART_PNG_EXPORT && (
            <button
              type="button"
              className="app-btn-outline text-[10px] py-1.5 px-2.5 gap-1 no-print"
              onClick={handleExportPng}
            >
              <Download size={12} /> {t("cf.png")}
            </button>
          )}
        <div className="flex items-center gap-1.5 p-1 rounded-lg border border-border bg-muted/40">
          {[
            { id: "bars" as ChartViewMode, label: t("cf.mode.bars"), icon: BarChart2 },
            { id: "area" as ChartViewMode, label: t("cf.mode.flow"), icon: LineChartIcon },
          ].map((mode) => {
            const Icon = mode.icon;
            const isActive = viewMode === mode.id;
            return (
              <button
                key={mode.id}
                onClick={() => setViewMode(mode.id)}
                className={cn(
                  "flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-all",
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-card/70"
                )}
              >
                <Icon size={13} />
                <span className="hidden sm:inline">{mode.label}</span>
              </button>
            );
          })}
        </div>
        </div>
      </div>

      {/* ── Mini KPI Banner ──────────────────────────────── */}
      <div className="grid grid-cols-3 divide-x divide-border border-b border-border bg-muted/20 text-center py-2.5 px-2 text-xs">
        <div>
          <span className="text-[10px] uppercase font-semibold text-muted-foreground block tracking-wider">
            {t("cf.avgIncome")}
          </span>
          <span className="font-semibold text-foreground mt-0.5 block">
            {format(avgIncome, true)}
            <span className="text-muted-foreground text-[10px] font-normal">{t("cf.perMo")}</span>
          </span>
        </div>
        <div>
          <span className="text-[10px] uppercase font-semibold text-muted-foreground block tracking-wider">
            {t("cf.avgExpenses")}
          </span>
          <span className="font-semibold text-foreground mt-0.5 block">
            {format(avgExpense, true)}
            <span className="text-muted-foreground text-[10px] font-normal">{t("cf.perMo")}</span>
          </span>
        </div>
        <div>
          <span className="text-[10px] uppercase font-semibold text-muted-foreground block tracking-wider">
            {t("cf.avgNet")}
          </span>
          <span
            className={cn(
              "font-bold mt-0.5 block",
              avgNet >= 0
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-destructive"
            )}
          >
            {avgNet >= 0 ? "+" : ""}
            {format(avgNet, true)}
            <span className="text-muted-foreground text-[10px] font-normal">{t("cf.perMo")}</span>
          </span>
        </div>
      </div>

      {/* ── Main Chart Area ──────────────────────────────── */}
      <div className="p-4 sm:p-5 pt-6">
        <ResponsiveContainer width="100%" height={260}>
          {viewMode === "bars" ? (
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 12, bottom: 0, left: 4 }}
              barGap={3}
            >
              <defs>
                <linearGradient id="incomeBarGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                </linearGradient>
                <linearGradient id="expenseBarGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(10 65% 58%)" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="hsl(10 65% 58%)" stopOpacity={0.4} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                strokeOpacity={0.7}
                vertical={false}
              />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                axisLine={{ stroke: "hsl(var(--border))" }}
                tickLine={false}
                dy={4}
              />
              <YAxis
                width={64}
                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => format(v, true)}
              />
              <Tooltip
                content={<CustomCashFlowTooltip />}
                cursor={{ fill: "hsl(var(--accent)/0.35)", rx: 6 }}
              />
              <Bar
                dataKey="income"
                name={t("cf.income")}
                fill="url(#incomeBarGrad)"
                stroke="hsl(var(--primary))"
                strokeWidth={1}
                radius={[5, 5, 1, 1]}
                maxBarSize={24}
              />
              <Bar
                dataKey="expense"
                name={t("cf.expense")}
                fill="url(#expenseBarGrad)"
                stroke="hsl(10 65% 58%)"
                strokeWidth={1}
                radius={[5, 5, 1, 1]}
                maxBarSize={24}
              />
            </BarChart>
          ) : (
            <AreaChart
              data={chartData}
              margin={{ top: 8, right: 12, bottom: 0, left: 4 }}
            >
              <defs>
                <linearGradient id="flowIncomeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.0} />
                </linearGradient>
                <linearGradient id="flowExpenseGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(10 65% 58%)" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="hsl(10 65% 58%)" stopOpacity={0.0} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                strokeOpacity={0.7}
                vertical={false}
              />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                axisLine={{ stroke: "hsl(var(--border))" }}
                tickLine={false}
                dy={4}
              />
              <YAxis
                width={64}
                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => format(v, true)}
              />
              <Tooltip
                content={<CustomCashFlowTooltip />}
                cursor={{ stroke: "hsl(var(--primary))", strokeWidth: 1, strokeDasharray: "3 3" }}
              />
              <Area
                type="monotone"
                dataKey="income"
                name={t("cf.income")}
                stroke="hsl(var(--primary))"
                strokeWidth={2.5}
                fillOpacity={1}
                fill="url(#flowIncomeGrad)"
                activeDot={{ r: 5, strokeWidth: 2, fill: "hsl(var(--card))", stroke: "hsl(var(--primary))" }}
              />
              <Area
                type="monotone"
                dataKey="expense"
                name={t("cf.expense")}
                stroke="hsl(10 65% 58%)"
                strokeWidth={2.5}
                fillOpacity={1}
                fill="url(#flowExpenseGrad)"
                activeDot={{ r: 5, strokeWidth: 2, fill: "hsl(var(--card))", stroke: "hsl(10 65% 58%)" }}
              />
            </AreaChart>
          )}
        </ResponsiveContainer>

        {/* ── Footer Legend & Highlight Callouts ─────────── */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-3.5 border-t border-border text-xs text-muted-foreground">
          {/* Legend Items */}
          <div className="flex items-center gap-5 flex-wrap">
            <div className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-sm shadow-sm"
                style={{ backgroundColor: "hsl(var(--primary))" }}
              />
              <span className="font-medium text-foreground">
                {t("cf.income")}: <span className="text-muted-foreground font-normal">{format(totalIncome, true)}</span>
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-sm shadow-sm"
                style={{ backgroundColor: "hsl(10 65% 58%)" }}
              />
              <span className="font-medium text-foreground">
                {t("cf.expenses")}: <span className="text-muted-foreground font-normal">{format(totalExpense, true)}</span>
              </span>
            </div>
          </div>

          {/* Quick highlight insights */}
          <div className="flex items-center gap-2 text-[11px]">
            {bestIncome && bestIncome.income > 0 && (
              <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-md border border-emerald-200 dark:border-emerald-800">
                <TrendingUp size={11} />
                {t("cf.peak", { month: bestIncome.name, amount: format(bestIncome.income, true) })}
              </span>
            )}
            {highestExpense && highestExpense.expense > 0 && (
              <span className="inline-flex items-center gap-1 text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 px-2 py-0.5 rounded-md border border-rose-200 dark:border-rose-800">
                {t("cf.maxExp", { month: highestExpense.name, amount: format(highestExpense.expense, true) })}
              </span>
            )}
          </div>
        </div>
      </div>
    </AppCard>
  );
}
